import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { FullScreenLoader } from "@/components/full-screen-loader";
import { AuthGuard } from "./auth-guard";
import { ErrorBoundary } from "./error-boundary";

// --- Layouts ---
const AuthenticatedLayout = lazy(() => import("./layouts/authenticated-layout"));
const PublicFlowLayout = lazy(() => import("./layouts/public-flow-layout"));
const PortfolioCollectionLayout = lazy(() => import("./layouts/portfolio-collection-layout"));

// --- Public pages ---
const LoginPage = lazy(() => import("@/pages/login/page"));
const Verify2FAPage = lazy(() => import("@/pages/login/verify-2fa/page"));
const SsoErrorPage = lazy(() => import("@/pages/login/sso-error/page"));
const SsoLinkPage = lazy(() => import("@/pages/login/sso-link/page"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password/page"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password/page"));
const OAuthConsentPage = lazy(() => import("@/pages/oauth/consent/page"));
const ClearPage = lazy(() => import("@/pages/clear/page"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/tos/page"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/privacy/page"));

// --- Public flow pages ---
const PublicFlowPage = lazy(() => import("@/pages/p/session/page"));
const PublicFlowAuthPage = lazy(() => import("@/pages/p/session/auth/page"));
const MemberLinkPage = lazy(() => import("@/pages/p/m/page"));
const PublicFlowSignPage = lazy(() => import("@/pages/p/session/sign/page"));
const PublicFlowSignSimplePage = lazy(() => import("@/pages/p/session/sign-simple/page"));
const PublicFlowErrorPage = lazy(() => import("@/pages/p/error/page"));
const PublicFlowThankYouPage = lazy(() => import("@/pages/p/thank-you/page"));
const PublicFlowSignatureCompletePage = lazy(() => import("@/pages/p/signature-complete/page"));
const PublicSharePage = lazy(() => import("@/pages/s/flow/page"));

// --- Protected: Dashboard ---
const DashboardPage = lazy(() => import("@/pages/page"));

// --- Protected: Clients ---
const ClientsPage = lazy(() => import("@/pages/clients/page"));
const ClientDetailPage = lazy(() => import("@/pages/clients/detail/page"));
const FlowInstancePage = lazy(() => import("@/pages/clients/detail/flows/instance/page"));
const FlowDocumentsPage = lazy(
  () => import("@/pages/clients/detail/flows/instance/documents/page"),
);

// --- Protected: Compliance ---
const CompliancePage = lazy(() => import("@/pages/compliance/page"));
const BatchLookupPage = lazy(() => import("@/pages/compliance/batch-lookup/page"));
const ComplianceApprovalsPage = lazy(() => import("@/pages/compliance/approvals/page"));
const ComplianceClientsReviewPage = lazy(() => import("@/pages/compliance/clients-review/page"));
const ComplianceFlowsReviewPage = lazy(() => import("@/pages/compliance/flows-review/page"));
const ComplianceIdentityChangesPage = lazy(
  () => import("@/pages/compliance/identity-changes/page"),
);

// --- Protected: Admin ---
const AdminPage = lazy(() => import("@/pages/admin/page"));
const AdminUsagePage = lazy(() => import("@/pages/admin/usage/page"));
const ComponentUsagePage = lazy(() => import("@/pages/admin/component-usage/page"));
const AdminTenantsPage = lazy(() => import("@/pages/admin/tenants/page"));
const TenantsOverviewPage = lazy(() => import("@/pages/admin/tenants-overview/page"));
const TimeseriesInstrumentsPage = lazy(() => import("@/pages/admin/timeseries-instruments/page"));

// --- Protected: Tenants ---
const TenantsPage = lazy(() => import("@/pages/tenants/page"));
const TenantDetailPage = lazy(() => import("@/pages/tenants/detail/page"));
const TenantUsersPage = lazy(() => import("@/pages/tenants/detail/users/page"));

// --- Protected: Settings (general) ---
const SettingsPage = lazy(() => import("@/pages/settings/page"));
const ProfilePage = lazy(() => import("@/pages/settings/profile/page"));
const ApiKeysPage = lazy(() => import("@/pages/settings/api-keys/page"));
const BillingLayout = lazy(() => import("@/pages/settings/billing/layout"));
const BillingPage = lazy(() => import("@/pages/settings/billing/page"));
const BillingInvoicesPage = lazy(() => import("@/pages/settings/billing/invoices/page"));
const AiSettingsPage = lazy(() => import("@/pages/settings/ai-settings/page"));
const SecurityPage = lazy(() => import("@/pages/settings/security/page"));
const NotificationSettingsPage = lazy(() => import("@/pages/settings/notifications/page"));
const SsoPage = lazy(() => import("@/pages/settings/sso/page"));
const ThemePage = lazy(() => import("@/pages/settings/theme/page"));
const RegionalSettingsPage = lazy(() => import("@/pages/settings/regional/page"));
const DashboardSettingsPage = lazy(() => import("@/pages/settings/dashboards/page"));
const OrganizationSettingsPage = lazy(() => import("@/pages/settings/organization/page"));
const CustomFieldsPage = lazy(() => import("@/pages/settings/custom-fields/page"));
const CustomInstrumentsPage = lazy(() => import("@/pages/settings/custom-instruments/page"));
const CombinedInstrumentsPage = lazy(() => import("@/pages/settings/combined-instruments/page"));
const WebhooksPage = lazy(() => import("@/pages/settings/webhooks/page"));
const ThirdPartySettingsPage = lazy(() => import("@/pages/settings/third-party/page"));
const SettingsClientsPage = lazy(() => import("@/pages/settings/clients/page"));
const UsersPage = lazy(() => import("@/pages/settings/users/page"));
const TeamsPage = lazy(() => import("@/pages/settings/teams/page"));
const TenantUsagePage = lazy(() => import("@/pages/settings/usage/page"));

// --- Protected: Settings > Flows ---
const FlowsPage = lazy(() => import("@/pages/settings/flows/page"));
const FlowDetailPage = lazy(() => import("@/pages/settings/flows/detail/page"));
const FlowPageEditorPage = lazy(() => import("@/pages/settings/flows/detail/pages/detail/page"));

// --- Protected: Settings > Financial Situation Models ---
const FSModelsPage = lazy(() => import("@/pages/settings/financial-situation-models/page"));
const FSModelDetailPage = lazy(
  () => import("@/pages/settings/financial-situation-models/detail/page"),
);

// --- Protected: Settings > PDF Templates ---
const PdfTemplatesPage = lazy(() => import("@/pages/settings/pdf-templates/page"));
const PdfTemplateDetailPage = lazy(() => import("@/pages/settings/pdf-templates/detail/page"));

// --- Protected: Settings > Risk Models ---
const RiskModelsPage = lazy(() => import("@/pages/settings/risk-models/page"));
const RiskModelDetailPage = lazy(() => import("@/pages/settings/risk-models/detail/page"));

// --- Protected: Settings > Portfolio Collections ---
const PortfoliosPage = lazy(() => import("@/pages/settings/portfolios/page"));
const PortfolioCollectionPage = lazy(() => import("@/pages/settings/portfolios/detail/page"));
const AssetClassesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/asset-classes/page"),
);
const CorrelationsPage = lazy(() => import("@/pages/settings/portfolios/detail/correlations/page"));
const CostsPage = lazy(() => import("@/pages/settings/portfolios/detail/costs/page"));
const PortfoliosListPage = lazy(
  () => import("@/pages/settings/portfolios/detail/portfolios-list/page"),
);
const DynamicPortfoliosPage = lazy(
  () => import("@/pages/settings/portfolios/detail/dynamic-portfolios/page"),
);
const DynamicPortfolioDetailPage = lazy(
  () => import("@/pages/settings/portfolios/detail/dynamic-portfolios/item/page"),
);
const PortfolioDetailPage = lazy(
  () => import("@/pages/settings/portfolios/detail/portfolio/item/page"),
);
const ProductDocumentsPage = lazy(
  () => import("@/pages/settings/portfolios/detail/product-documents/page"),
);
const ProductTypesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/product-types/page"),
);
const CollectionGoalTypesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/goal-types/page"),
);
const CollectionWrapperTypesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/wrapper-types/page"),
);
const CollectionClientPreferencesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/client-preferences/page"),
);
const CollectionPlatformsPage = lazy(
  () => import("@/pages/settings/portfolios/detail/platforms/page"),
);
const CollectionSustainabilityPage = lazy(
  () => import("@/pages/settings/portfolios/detail/sustainability/page"),
);
const CollectionMandatesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/mandates/page"),
);
const ProductsPage = lazy(() => import("@/pages/settings/portfolios/detail/products/page"));
const ProductsGeneralPage = lazy(
  () => import("@/pages/settings/portfolios/detail/products/general/page"),
);
const ProductsSuitabilityPage = lazy(
  () => import("@/pages/settings/portfolios/detail/products/suitability/page"),
);
const ProductsDocumentsPage = lazy(
  () => import("@/pages/settings/portfolios/detail/products/documents/page"),
);
const ProductsAssetClassesPage = lazy(
  () => import("@/pages/settings/portfolios/detail/products/asset-classes/page"),
);
const ProductsCostPage = lazy(
  () => import("@/pages/settings/portfolios/detail/products/cost/page"),
);
const CollectionSettingsPage = lazy(
  () => import("@/pages/settings/portfolios/detail/settings/page"),
);

// --- Protected: Ideas ---
const IdeasPage = lazy(() => import("@/pages/ideas/page"));

// --- Protected: Editor ---
const EditorPage = lazy(() => import("@/pages/editor-00/page"));

export function AppRouter() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/verify-2fa" element={<Verify2FAPage />} />
          <Route path="/login/sso-error" element={<SsoErrorPage />} />
          <Route path="/login/sso-link" element={<SsoLinkPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          <Route path="/clear" element={<ClearPage />} />
          <Route path="/tos" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          {/* Public flow routes */}
          <Route path="/p/error" element={<PublicFlowErrorPage />} />
          <Route path="/p/thank-you" element={<PublicFlowThankYouPage />} />
          <Route path="/p/signature-complete" element={<PublicFlowSignatureCompletePage />} />
          <Route path="/p/m/:token" element={<MemberLinkPage />} />
          <Route element={<PublicFlowLayout />}>
            <Route path="/p/:token" element={<PublicFlowPage />} />
            <Route path="/p/:token/auth" element={<PublicFlowAuthPage />} />
            <Route path="/p/:token/sign" element={<PublicFlowSignPage />} />
            <Route path="/p/:token/sign-simple" element={<PublicFlowSignSimplePage />} />
            {/* QL-505 — the member-session flow. Separate route (never
                `/p/:token`) so a member session is never handed the group's
                own public token: the callback redirects here, and the page
                keeps the member session under its own localStorage key. */}
            <Route path="/p/m/:token/flow" element={<PublicFlowPage isMemberRoute />} />
          </Route>
          <Route path="/s/:flowId" element={<PublicSharePage />} />

          {/* Protected routes — wrapped in AuthGuard */}
          <Route element={<AuthGuard />}>
            <Route element={<AuthenticatedLayout />}>
              <Route path="/" element={<DashboardPage />} />

              {/* Clients */}
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/clients/:id/flows/:instanceId" element={<FlowInstancePage />} />
              <Route
                path="/clients/:id/flows/:instanceId/process"
                element={<FlowDocumentsPage />}
              />

              {/* Compliance */}
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/compliance/batch-lookup" element={<BatchLookupPage />} />
              <Route path="/compliance/approvals" element={<ComplianceApprovalsPage />} />
              <Route path="/compliance/clients" element={<ComplianceClientsReviewPage />} />
              <Route path="/compliance/flows" element={<ComplianceFlowsReviewPage />} />
              <Route
                path="/compliance/identity-changes"
                element={<ComplianceIdentityChangesPage />}
              />

              {/* Admin */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/usage" element={<AdminUsagePage />} />
              <Route path="/admin/component-usage" element={<ComponentUsagePage />} />
              <Route path="/admin/tenants" element={<AdminTenantsPage />} />
              <Route path="/admin/tenants-overview" element={<TenantsOverviewPage />} />
              <Route path="/admin/timeseries-instruments" element={<TimeseriesInstrumentsPage />} />

              {/* Tenants */}
              <Route path="/tenants" element={<TenantsPage />} />
              <Route path="/tenants/:id" element={<TenantDetailPage />} />
              <Route path="/tenants/:id/users" element={<TenantUsersPage />} />

              {/* Settings */}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/profile" element={<ProfilePage />} />
              <Route path="/settings/api-keys" element={<ApiKeysPage />} />
              <Route element={<BillingLayout />}>
                <Route path="/settings/billing" element={<BillingPage />} />
                <Route path="/settings/billing/invoices" element={<BillingInvoicesPage />} />
              </Route>
              <Route path="/settings/ai-settings" element={<AiSettingsPage />} />
              <Route path="/settings/security" element={<SecurityPage />} />
              <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
              <Route path="/settings/sso" element={<SsoPage />} />
              <Route path="/settings/theme" element={<ThemePage />} />
              <Route path="/settings/regional" element={<RegionalSettingsPage />} />
              <Route path="/settings/dashboards" element={<DashboardSettingsPage />} />
              <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
              {/* Legacy redirect: Languages was renamed Regional settings (QL-108) */}
              <Route
                path="/settings/languages"
                element={<Navigate to="/settings/regional" replace />}
              />
              <Route path="/settings/custom-fields" element={<CustomFieldsPage />} />
              <Route path="/settings/custom-instruments" element={<CustomInstrumentsPage />} />
              <Route path="/settings/combined-instruments" element={<CombinedInstrumentsPage />} />
              {/* Legacy redirect: combined instruments moved out of admin (QL-271) */}
              <Route
                path="/admin/combined-instruments"
                element={<Navigate to="/settings/combined-instruments" replace />}
              />
              <Route path="/settings/webhooks" element={<WebhooksPage />} />
              <Route path="/settings/third-party" element={<ThirdPartySettingsPage />} />
              {/* Legacy redirects to the renamed 3rd-party page */}
              <Route
                path="/settings/compliance"
                element={<Navigate to="/settings/third-party" replace />}
              />
              <Route
                path="/settings/trapets"
                element={<Navigate to="/settings/third-party" replace />}
              />
              <Route path="/settings/clients" element={<SettingsClientsPage />} />
              <Route path="/settings/users" element={<UsersPage />} />
              <Route path="/settings/teams" element={<TeamsPage />} />
              <Route path="/settings/usage" element={<TenantUsagePage />} />

              {/* Settings: Flows */}
              <Route path="/settings/flows" element={<FlowsPage />} />
              <Route path="/settings/flows/:id" element={<FlowDetailPage />} />
              <Route path="/settings/flows/:id/pages/:pageId" element={<FlowPageEditorPage />} />

              {/* Settings: Financial Situation Models */}
              <Route path="/settings/financial-situation-models" element={<FSModelsPage />} />
              <Route
                path="/settings/financial-situation-models/:id"
                element={<FSModelDetailPage />}
              />

              {/* Settings: PDF Templates */}
              <Route path="/settings/pdf-templates" element={<PdfTemplatesPage />} />
              <Route path="/settings/pdf-templates/:id" element={<PdfTemplateDetailPage />} />

              {/* Settings: Risk Models */}
              <Route path="/settings/risk-models" element={<RiskModelsPage />} />
              <Route path="/settings/risk-models/:id" element={<RiskModelDetailPage />} />

              {/* Settings: Portfolio Collections */}
              <Route path="/settings/portfolios" element={<PortfoliosPage />} />
              <Route element={<PortfolioCollectionLayout />}>
                <Route path="/settings/portfolios/:id" element={<PortfolioCollectionPage />} />
                <Route
                  path="/settings/portfolios/:id/asset-classes"
                  element={<AssetClassesPage />}
                />
                <Route
                  path="/settings/portfolios/:id/correlations"
                  element={<CorrelationsPage />}
                />
                <Route path="/settings/portfolios/:id/costs" element={<CostsPage />} />
                <Route
                  path="/settings/portfolios/:id/portfolios-list"
                  element={<PortfoliosListPage />}
                />
                <Route
                  path="/settings/portfolios/:id/portfolio/:portfolioId"
                  element={<PortfolioDetailPage />}
                />
                <Route
                  path="/settings/portfolios/:id/dynamic-portfolios"
                  element={<DynamicPortfoliosPage />}
                />
                <Route
                  path="/settings/portfolios/:id/dynamic-portfolios/:dynamicPortfolioId"
                  element={<DynamicPortfolioDetailPage />}
                />
                <Route
                  path="/settings/portfolios/:id/product-documents"
                  element={<ProductDocumentsPage />}
                />
                <Route
                  path="/settings/portfolios/:id/product-types"
                  element={<ProductTypesPage />}
                />
                <Route
                  path="/settings/portfolios/:id/goal-types"
                  element={<CollectionGoalTypesPage />}
                />
                <Route
                  path="/settings/portfolios/:id/wrapper-types"
                  element={<CollectionWrapperTypesPage />}
                />
                <Route
                  path="/settings/portfolios/:id/client-preferences"
                  element={<CollectionClientPreferencesPage />}
                />
                <Route
                  path="/settings/portfolios/:id/platforms"
                  element={<CollectionPlatformsPage />}
                />
                <Route
                  path="/settings/portfolios/:id/sustainability"
                  element={<CollectionSustainabilityPage />}
                />
                <Route
                  path="/settings/portfolios/:id/mandates"
                  element={<CollectionMandatesPage />}
                />
                <Route path="/settings/portfolios/:id/products" element={<ProductsPage />}>
                  <Route index element={<Navigate to="general" replace />} />
                  <Route path="general" element={<ProductsGeneralPage />} />
                  <Route path="suitability" element={<ProductsSuitabilityPage />} />
                  <Route path="documents" element={<ProductsDocumentsPage />} />
                  <Route path="asset-classes" element={<ProductsAssetClassesPage />} />
                  <Route path="cost" element={<ProductsCostPage />} />
                </Route>
                <Route
                  path="/settings/portfolios/:id/settings"
                  element={<CollectionSettingsPage />}
                />
              </Route>

              {/* Ideas */}
              <Route path="/ideas" element={<IdeasPage />} />

              {/* Editor */}
              <Route path="/editor-00" element={<EditorPage />} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
